import { useEffect } from "react";
import { App, Button, Card, Col, Form, Input, Row, Space, Switch, Typography } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { HomeBanner, HomeFeature } from "@glow-price/shared";

import { homeSettingsApi } from "../api/resources";
import { ImageUpload } from "../components/ImageUpload";

/**
 * Phase 6D-2 编辑精选 / 标签管理。
 *
 * 当前覆盖的写口：
 *   - `/api/admin/home-settings/feature` —— 首页"编辑精选"大图卡（保存写入 site_settings.home_feature）
 *   - `/api/admin/home-settings/banners` —— 首页顶部 3 张轮播 Banner（"今日好价"/"近期最低"/"大牌精选" 等）
 *
 * TODO(api): 当前 banners 仍是固定 3 槽位的运营卡片，若未来需要更复杂的
 * "标签/精选合集" CRUD（多个、可排序、按类别动态生成），需要后端新增
 * `/api/admin/editorial-collections` 或 `home_banners` 改为支持任意条数。
 * 在此之前，这个页面仅复用既有的 home-settings 接口。
 */
const BANNER_VARIANTS = [
  { name: "酒红", bg: "linear-gradient(135deg, #4a1c2a 0%, #6d2839 100%)" },
  { name: "炭墨", bg: "linear-gradient(135deg, #2a2520 0%, #4a3f33 100%)" },
  { name: "酒红→金", bg: "linear-gradient(135deg, #6d2839 0%, #b8956b 100%)" },
];

type BannersForm = { items: HomeBanner[] };

export function EditorialTagsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const [featureForm] = Form.useForm<HomeFeature>();
  const [bannerForm] = Form.useForm<BannersForm>();

  const featureQuery = useQuery({
    queryKey: ["home-feature"],
    queryFn: () => homeSettingsApi.getFeature(),
  });
  const bannersQuery = useQuery({
    queryKey: ["home-banners"],
    queryFn: () => homeSettingsApi.getBanners(),
  });

  useEffect(() => {
    if (featureQuery.data) featureForm.setFieldsValue(featureQuery.data);
  }, [featureQuery.data, featureForm]);
  useEffect(() => {
    if (bannersQuery.data) bannerForm.setFieldsValue({ items: bannersQuery.data.items });
  }, [bannersQuery.data, bannerForm]);

  const featureMutation = useMutation({
    mutationFn: (values: HomeFeature) => homeSettingsApi.putFeature(values),
    onSuccess: () => {
      message.success("编辑精选已保存，下次进入首页生效");
      qc.invalidateQueries({ queryKey: ["home-feature"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  const bannersMutation = useMutation({
    mutationFn: (items: HomeBanner[]) => homeSettingsApi.putBanners(items),
    onSuccess: () => {
      message.success("Banner 已保存");
      qc.invalidateQueries({ queryKey: ["home-banners"] });
    },
    onError: (err: Error) => message.error(err.message),
  });

  return (
    <div>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        编辑精选与标签
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
        管理首页的"编辑精选"大图卡，以及顶部 3 张 Banner（"今日好价" / "近期最低" / "大牌精选" 等运营卡片）。
      </Typography.Paragraph>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="编辑精选（home_feature）" loading={featureQuery.isLoading}>
            <Form<HomeFeature>
              form={featureForm}
              layout="vertical"
              initialValues={featureQuery.data}
              onFinish={(values) => featureMutation.mutate(values)}
            >
              <Form.Item name="enabled" label="启用" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item
                name="eyebrow"
                label="英文小标 (Eyebrow)"
                rules={[{ required: true, max: 40 }]}
              >
                <Input placeholder="Editor's Pick" />
              </Form.Item>
              <Form.Item name="title" label="标题" rules={[{ required: true, max: 60 }]}>
                <Input placeholder="本周编辑精选" />
              </Form.Item>
              <Form.Item name="body" label="副文案" rules={[{ required: true, max: 160 }]}>
                <Input.TextArea rows={2} placeholder="奢护肌底新品 + 经典香氛特惠" />
              </Form.Item>
              <Form.Item name="cta" label="按钮文案" rules={[{ required: true, max: 20 }]}>
                <Input placeholder="探索精选" />
              </Form.Item>
              <Form.Item
                name="linkTo"
                label="跳转路径"
                rules={[{ required: true, max: 120 }]}
              >
                <Input placeholder="/top-deals" />
              </Form.Item>
              <Form.Item
                name="imageUrl"
                label="背景图"
                rules={[{ required: true, type: "url" }]}
              >
                <ImageUpload folder="home-feature" width={120} height={80} />
              </Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={featureMutation.isPending}
                >
                  保存编辑精选
                </Button>
                <Button
                  onClick={() => featureForm.resetFields()}
                  disabled={featureMutation.isPending}
                >
                  撤销
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card title="首页 Banner（home_banners）" loading={bannersQuery.isLoading}>
            <Typography.Paragraph type="secondary" style={{ marginTop: -4 }}>
              3 个固定槽位，渐变背景按槽位预设；文案 / 跳转可改。
            </Typography.Paragraph>
            <Form<BannersForm>
              form={bannerForm}
              layout="vertical"
              initialValues={bannersQuery.data}
              onFinish={(values) => bannersMutation.mutate(values.items)}
            >
              {[0, 1, 2].map((idx) => (
                <Card
                  key={idx}
                  size="small"
                  style={{ marginBottom: 12 }}
                  title={
                    <span>
                      <span
                        style={{
                          display: "inline-block",
                          width: 12,
                          height: 12,
                          borderRadius: 3,
                          background: BANNER_VARIANTS[idx]!.bg,
                          marginRight: 8,
                          verticalAlign: "middle",
                        }}
                      />
                      槽 #{idx + 1}（{BANNER_VARIANTS[idx]!.name}）
                    </span>
                  }
                >
                  <Form.Item
                    name={["items", idx, "title"]}
                    label="标题"
                    rules={[{ required: true, max: 30 }]}
                  >
                    <Input placeholder="今日好价 / 近期最低 / 大牌精选" />
                  </Form.Item>
                  <Form.Item
                    name={["items", idx, "sub"]}
                    label="副文案"
                    rules={[{ required: true, max: 60 }]}
                  >
                    <Input placeholder="中免日上 · 多渠道实时比价" />
                  </Form.Item>
                  <Form.Item
                    name={["items", idx, "linkTo"]}
                    label="跳转路径"
                    rules={[{ required: true, max: 120 }]}
                  >
                    <Input placeholder="/top-deals" />
                  </Form.Item>
                </Card>
              ))}
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={bannersMutation.isPending}
                >
                  保存 3 张 Banner
                </Button>
                <Button
                  onClick={() => bannerForm.resetFields()}
                  disabled={bannersMutation.isPending}
                >
                  撤销
                </Button>
              </Space>
            </Form>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
